import { isNil, isUndefined } from 'lodash-es';

import { WithId } from '../../../types/with-id.js';
import {
  DEFAULT_MARKER_PRIORITY,
  DEFAULT_TEXT_OVERFLOW,
} from '../constants.js';
import {
  AnnotationsMarkerConfig,
  NumericalMarkerProps,
  TimeMarkerProps,
} from '../types/annotations-marker-props.js';
import { AnnotationsTrackConfig } from '../types/annotations-track-props.js';
import { AnnotationsMarkerGroup } from '../types/annotations.js';
import type { TextOverflowOptions } from '../types/marker.js';
import { type AnnotationsScale } from './calculate-marker-dimension.js';
import { getMarkerPixelExtent } from './get-marker-pixel-extent.js';
import { isNumericalMarker } from './is-numerical-marker.js';
import { parseDateNumberToNumber } from './parse-date-number-to-number.js';
import { serializeMarkerRange } from './serialize-marker-range.js';

const areNumerical = (
  markers: AnnotationsMarkerConfig[],
): markers is WithId<AnnotationsMarkerConfig & NumericalMarkerProps>[] => {
  return isNumericalMarker(markers[0]);
};

const areTime = (
  markers: AnnotationsMarkerConfig[],
): markers is WithId<AnnotationsMarkerConfig & TimeMarkerProps>[] => {
  return !isNumericalMarker(markers[0]);
};

const toNumber = (value: number | Date) =>
  typeof value === 'number' ? value : value.getTime();

const outOfBounds = (
  marker: AnnotationsMarkerConfig,
  domain?: number[] | Date[],
) => {
  if (isUndefined(domain)) {
    return false;
  }

  const d0 = toNumber(domain[0]);
  const d1 = toNumber(domain[1]);
  const lowerBound = Math.min(d0, d1);
  const upperBound = Math.max(d0, d1);

  const start = toNumber(marker.start);
  const end = isNil(marker.end) ? start : toNumber(marker.end);

  return Math.min(start, end) > upperBound || Math.max(start, end) < lowerBound;
};

const handleNonOverlappingCluster = (
  processedMarkers: Map<string, AnnotationsMarkerGroup[]>,
  cluster: AnnotationsMarkerConfig[],
  markerKey: string,
) => {
  processedMarkers.set(markerKey, [createGroup(cluster, markerKey)]);
};

const growGroupPixelRight = (
  currentPixelRight: number,
  markerPixelExtent: [number, number] | undefined,
): number => {
  if (markerPixelExtent === undefined) {
    return currentPixelRight;
  }
  return isNaN(currentPixelRight)
    ? markerPixelExtent[1]
    : Math.max(currentPixelRight, markerPixelExtent[1]);
};

const isVisuallyColliding = (
  lastGroupPixelRight: number,
  markerPixelExtent: [number, number] | undefined,
): boolean =>
  markerPixelExtent !== undefined &&
  !isNaN(lastGroupPixelRight) &&
  markerPixelExtent[0] <= lastGroupPixelRight;

const collidesWithLastGroup = (
  lastGroupStart: number,
  lastGroupEnd: number,
  markerStart: number,
  markerEnd: number,
  lastGroupPixelRight: number,
  markerPixelExtent: [number, number] | undefined,
): boolean =>
  overlapsWithTheLastGroup(
    lastGroupStart,
    lastGroupEnd,
    markerStart,
    markerEnd,
  ) || isVisuallyColliding(lastGroupPixelRight, markerPixelExtent);

interface GroupingState {
  lastProcessedGroupKey: string;
  lastGroupPixelRight: number;
  // Data range used only for detecting *data*-based overlap with the next
  // marker. Kept separate from pixel-based collisions on purpose: a marker
  // that joins the group solely because its rendered label visually collides
  // (not because its time range overlaps) must not widen this range, or a
  // later, genuinely unrelated marker could be falsely matched against the
  // resulting inflated range purely on the data check.
  lastGroupDataStart: number;
  lastGroupDataEnd: number;
}

const processClusterIntoGroups = (
  processedMarkers: Map<string, AnnotationsMarkerGroup[]>,
  cluster: AnnotationsMarkerConfig[],
  trackIndex: number,
  markerPixelExtent: [number, number] | undefined,
  state: GroupingState,
): GroupingState => {
  const representative = cluster[0];
  const markerKey = serializeMarkerRange(representative, trackIndex);
  const markerStart = parseDateNumberToNumber(representative.start);
  const markerEnd = representative.end
    ? parseDateNumberToNumber(representative.end)
    : markerStart;

  if (!state.lastProcessedGroupKey) {
    handleNonOverlappingCluster(processedMarkers, cluster, markerKey);
    return {
      lastProcessedGroupKey: markerKey,
      lastGroupPixelRight: markerPixelExtent?.[1] ?? NaN,
      lastGroupDataStart: markerStart,
      lastGroupDataEnd: markerEnd,
    };
  }

  const collides = collidesWithLastGroup(
    state.lastGroupDataStart,
    state.lastGroupDataEnd,
    markerStart,
    markerEnd,
    state.lastGroupPixelRight,
    markerPixelExtent,
  );
  if (!collides) {
    handleNonOverlappingCluster(processedMarkers, cluster, markerKey);
    return {
      lastProcessedGroupKey: markerKey,
      lastGroupPixelRight: markerPixelExtent?.[1] ?? NaN,
      lastGroupDataStart: markerStart,
      lastGroupDataEnd: markerEnd,
    };
  }

  const groups = processedMarkers.get(state.lastProcessedGroupKey) ?? [];
  const lastGroupPixelRight = growGroupPixelRight(
    state.lastGroupPixelRight,
    markerPixelExtent,
  );
  const hasDataOverlap = overlapsWithTheLastGroup(
    state.lastGroupDataStart,
    state.lastGroupDataEnd,
    markerStart,
    markerEnd,
  );
  const lastGroupDataStart = hasDataOverlap
    ? Math.min(state.lastGroupDataStart, markerStart)
    : markerStart;
  const lastGroupDataEnd = hasDataOverlap
    ? Math.max(state.lastGroupDataEnd, markerEnd)
    : markerEnd;

  groups.push(createGroup(cluster, markerKey));
  return {
    lastProcessedGroupKey: state.lastProcessedGroupKey,
    lastGroupPixelRight,
    lastGroupDataStart,
    lastGroupDataEnd,
  };
};

/**
 * Process annotation markers.
 * That includes sorting and grouping by time.
 *
 * @param trackProps - track props
 * @param trackIndex - track index
 * @param domain - visible domain
 * @param scale - detects markers whose rendered labels
 * visually collide even though their data ranges don't overlap
 * @param textOverflow - how range marker labels behave when they don't fit
 * only relevant together with `scale`
 * @returns processed markers
 */
export function processAnnotationTracksData(
  trackProps: AnnotationsTrackConfig,
  trackIndex: number,
  domain?: Date[] | number[],
  scale?: AnnotationsScale,
  textOverflow: TextOverflowOptions = DEFAULT_TEXT_OVERFLOW,
): Map<string, AnnotationsMarkerGroup[]> {
  const { markers } = trackProps;
  const processedMarkers = new Map<string, AnnotationsMarkerGroup[]>();
  if (markers.length === 0) {
    return processedMarkers;
  }
  const sortedMarkers = sortMarkers(markers);
  const visibleMarkers = sortedMarkers.filter(
    (marker) => !marker.hidden && !outOfBounds(marker, domain),
  );
  const clusters = groupMarkersByIdenticalRange(visibleMarkers, trackIndex);
  let state: GroupingState = {
    lastProcessedGroupKey: '',
    lastGroupPixelRight: NaN,
    lastGroupDataStart: NaN,
    lastGroupDataEnd: NaN,
  };
  for (const cluster of clusters) {
    const markerPixelExtent = scale
      ? getMarkerPixelExtent(cluster[0], scale, textOverflow, cluster.length)
      : undefined;
    state = processClusterIntoGroups(
      processedMarkers,
      cluster,
      trackIndex,
      markerPixelExtent,
      state,
    );
  }
  return processedMarkers;
}

/**
 * Buckets contiguous markers that share the same start/end (and therefore
 * render as a single marker, possibly showing a count badge) into clusters.
 * Markers are expected to already be sorted by start/end, which guarantees
 * identical-range markers are adjacent.
 *
 * @param markers - sorted, already-visible markers
 * @param trackIndex - track index
 */
const groupMarkersByIdenticalRange = (
  markers: AnnotationsMarkerConfig[],
  trackIndex: number,
): AnnotationsMarkerConfig[][] => {
  const clusters: AnnotationsMarkerConfig[][] = [];
  let lastKey: string | undefined;
  for (const marker of markers) {
    const key = serializeMarkerRange(marker, trackIndex);
    if (key === lastKey) {
      clusters[clusters.length - 1].push(marker);
    } else {
      clusters.push([marker]);
      lastKey = key;
    }
  }
  return clusters;
};

/**
 * Sorts markers depending of type
 *
 * @param markers -
 */
const sortMarkers = (markers: AnnotationsMarkerConfig[]) => {
  if (areNumerical(markers)) {
    return sortNumericalMarkers(markers);
  } else if (areTime(markers)) {
    return sortTimeMarkers(markers);
  } else {
    throw new Error('Unsupported type of markers');
  }
};

/**
 * Sorts markers by start value and end value.
 *
 * @param markers -
 */
const sortNumericalMarkers = (
  markers: WithId<AnnotationsMarkerConfig & NumericalMarkerProps>[],
): AnnotationsMarkerConfig[] => {
  return markers.sort((a, b) => {
    const aEnd = a.end ?? a.start;
    const bEnd = b.end ?? b.start;

    if (a.start === b.start) {
      return aEnd - bEnd;
    }

    return a.start - b.start;
  });
};

/**
 * Sorts markers by start time and end time.
 *
 * @param markers -
 */
const sortTimeMarkers = (
  markers: WithId<AnnotationsMarkerConfig & TimeMarkerProps>[],
): AnnotationsMarkerConfig[] => {
  return markers.sort((a, b) => {
    const aStart = a.start.getTime();
    const bStart = b.start.getTime();
    const aEnd = a.end ? a.end.getTime() : aStart;
    const bEnd = b.end ? b.end.getTime() : bStart;

    if (aStart === bStart) {
      return aEnd - bEnd;
    }

    return a.start.getTime() - b.start.getTime();
  });
};

/**
 * Creates a new group from a cluster of identical-range annotations
 *
 * @param markers - the cluster of annotations sharing the same start/end
 * @param key - key of the group ("start_end" of the annotations)
 * @returns a new group containing the cluster's annotations
 */
const createGroup = (
  markers: AnnotationsMarkerConfig[],
  key: string,
): AnnotationsMarkerGroup => ({
  markers,
  priority: Math.max(
    ...markers.map((marker) => marker.priority ?? DEFAULT_MARKER_PRIORITY),
  ),
  key,
});

/**
 * Checks if the annotation overlaps with the last group
 *
 * @param groupStart - start of the last group
 * @param groupEnd - end of the last group
 * @param start - start of the annotation
 * @param end - end of the annotation
 */
const overlapsWithTheLastGroup = (
  groupStart: number,
  groupEnd: number,
  start: number,
  end: number,
): boolean =>
  (start >= groupStart && start <= groupEnd) ||
  (end >= groupStart && end <= groupEnd);
